import { useTranslation } from 'react-i18next';
import type { IdentityAnswer, IdentityQuestion } from '../../types/index';

/** The identity question (sculpt-api PLAN-workbook-identity.md §4.2): the file is open from a name or a place its
 *  workbook has not seen. It takes the pane, like the first index, because nothing is read and the chat waits
 *  until it is answered (D6). It never shows a path — only what changed and the previous name (D19). Closing it
 *  is "same file, for this session" (D5). */
export function IdentityCard({ question, error, onAnswer }: { question: IdentityQuestion; error: string | null; onAnswer: (answer: IdentityAnswer) => void }) {
  const { t, i18n } = useTranslation();
  const name = question.known_name;
  const changed = question.name_changed && question.path_changed ? 'both' : question.name_changed ? 'name' : 'place';
  const resume = question.resume;
  const shared = question.owner === 'other';
  const lostId = !!question.lost_id && !!resume;
  const answers: { key: IdentityAnswer; label: string; hint?: string; primary?: boolean }[] = lostId
    // A file where a known one used to be, with other sheets: most likely a new one, so another workbook comes first.
    ? [
        { key: 'other', label: t('identity.other'), hint: t('identity.otherHint'), primary: true },
        { key: 'resume', label: t('identity.resume', { name: resume.name }), hint: t('identity.lostIdResumeHint', { name: resume.name }) },
      ]
    : shared
    ? [
        { key: 'same', label: t('identity.shared'), hint: t('identity.sharedHint', { name }), primary: true },
        ...(question.copy_answers === 2
          ? [{ key: 'fork' as const, label: t('identity.ownCopyVersion'), hint: t('identity.forkHint') },
             { key: 'other' as const, label: t('identity.ownCopyOther'), hint: t('identity.otherHint') }]
          : [{ key: 'other' as const, label: t('identity.ownCopy'), hint: t('identity.ownCopyHint') }]),
      ]
    : [
        ...(resume
          ? [{ key: 'resume' as const, label: t('identity.resume', { name: resume.name }), primary: true,
               hint: resume.parent_name
                 ? t('identity.resumeHint', { parent: resume.parent_name, date: resume.forked_at ? new Date(resume.forked_at).toLocaleDateString(i18n.language) : '' })
                 : undefined }]
          : []),
        { key: 'same', label: t('identity.same'), hint: t('identity.sameHint'), primary: !resume },
        { key: 'fork', label: t('identity.fork', { name }), hint: t('identity.forkHint') },
        { key: 'other', label: t('identity.other'), hint: t('identity.otherHint') },
      ];

  return (
    <div className="flex flex-1 flex-col justify-center px-5 py-6" data-testid="identity-card">
      <div className="mx-auto flex w-full max-w-[320px] flex-col gap-3">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[13px] font-medium text-ink">{t(shared ? 'identity.titleShared' : 'identity.title')}</span>
          <button type="button" onClick={() => onAnswer('dismiss')} className="text-[14px] text-muted hover:text-ink" title={t(lostId ? 'identity.dismissLostId' : 'identity.dismiss')} aria-label={t(lostId ? 'identity.dismissLostId' : 'identity.dismiss')}>
            ×
          </button>
        </div>
        <p className="text-[12.5px] leading-[1.45] text-muted">{t(lostId ? 'identity.lostId' : shared ? 'identity.whyShared' : `identity.changed.${changed}`, { name })}</p>
        <div className="flex flex-col gap-2">
          {answers.map((a) => (
            <button
              key={a.key}
              type="button"
              data-answer={a.key}
              onClick={() => onAnswer(a.key)}
              className={`flex flex-col gap-0.5 rounded-[6px] border px-3 py-2 text-left ${a.primary ? 'border-accent bg-accent text-white hover:bg-accent-hover' : 'border-line-btn bg-white text-ink hover:bg-hover-soft'}`}
            >
              <span className="text-[13px] font-medium">{a.label}</span>
              {a.hint && <span className={`text-[11.5px] leading-[1.4] ${a.primary ? 'text-white/80' : 'text-muted'}`}>{a.hint}</span>}
            </button>
          ))}
        </div>
        {error && <p role="alert" className="text-[12px] text-danger">{t('identity.failed', { error })}</p>}
      </div>
    </div>
  );
}
